import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Tooltip,
  Paper,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, ContentCopy as CopyIcon, VpnKey as ResetIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import PageHeader from '../../base/PageHeader';
import { BaseButton, BaseTextField, BaseSelect } from '../../base';
import BaseModal from '../../base/BaseModal';
import { BreadcrumbItem } from '../../../../types/admin.types';
import UserService from '../../../../services/UserService';
import AuthService from '../../../../services/AuthService';
import type { User } from '../../../../services/UserService';

// --- Design tokens (same as SchemaTab) --------------------------------------
const FONT = '"Manrope", sans-serif';
const ACCENT = '#005e5d';
const ACCENT_LIGHT = '#005e5d14';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';
const BORDER = '#E6E7EB';
const SURFACE = '#F2F3F5';

const headerCellSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: TEXT_SECONDARY,
  backgroundColor: SURFACE,
  borderBottom: `1px solid ${BORDER}`,
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontFamily: FONT,
  fontSize: '0.875rem',
  color: TEXT_PRIMARY,
  borderBottom: `1px solid ${BORDER}`,
  py: 1.5,
  px: 2,
};

// --- Role / Status badges ----------------------------------------------------
const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  user: 'User',
};

const ROLE_COLOR: Record<string, { bg: string; text: string }> = {
  admin: { bg: '#FFEBEE', text: '#C62828' },
  manager: { bg: '#FFF8E1', text: '#F57F17' },
  user: { bg: SURFACE, text: TEXT_SECONDARY },
};

const STATUS_COLOR: Record<string, { bg: string; text: string }> = {
  active: { bg: '#E8F5E9', text: '#2E7D32' },
  inactive: { bg: '#FFEBEE', text: '#C62828' },
  resetpassword: { bg: '#FFF3E0', text: '#E65100' },
};

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const c = ROLE_COLOR[role] ?? ROLE_COLOR.user;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.4,
        borderRadius: '6px',
        backgroundColor: c.bg,
      }}
    >
      <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: c.text }}>
        {ROLE_LABEL[role] ?? role}
      </Typography>
    </Box>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const c = STATUS_COLOR[status] ?? { bg: SURFACE, text: TEXT_SECONDARY };
  const statusLabel = status === 'resetpassword' ? 'Reset Password' : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.4, borderRadius: '6px', backgroundColor: c.bg }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c.text, flexShrink: 0 }} />
      <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: c.text }}>
        {statusLabel}
      </Typography>
    </Box>
  );
};

// --- Edit User Modal ---------------------------------------------------------
interface EditUserModalProps {
  user: User | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: User) => void;
  isAdmin: boolean;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, open, onClose, onSaved, isAdmin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<User['role']>('user');
  const [status, setStatus] = useState<User['status']>('active');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setRole(user.role);
      setStatus(user.status);
      setError(null);
    }
  }, [user]);

  const handleSave = async () => {
    if (!user?._id) return;
    if (!isAdmin) {
      setError('Only admins can edit users');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await UserService.updateUser(user._id, { name, email, role, status });
      onSaved(res.data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Edit User"
      maxWidth="sm"
      onSubmit={handleSave}
      isLoading={saving}
      submitButtonText="Save"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <BaseTextField
          label="Name"
          value={name}
          onChange={(e) => setName((e.target as HTMLInputElement).value)}
          fullWidth
          size="small"
        />
        <BaseTextField
          label="Email"
          value={email}
          onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
          fullWidth
          size="small"
          type="email"
        />
        <BaseSelect
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as User['role'])}
          options={[
            { label: 'User', value: 'user' },
            { label: 'Manager', value: 'manager' },
            { label: 'Admin', value: 'admin' },
          ]}
        />
        <BaseSelect
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as User['status'])}
          options={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
            { label: 'Reset Password', value: 'resetpassword' },
          ]}
        />
        {error && (
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: '#D32F2F' }}>{error}</Typography>
        )}
      </Box>
    </BaseModal>
  );
};

// --- Create User Modal ------------------------------------------------------
interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (user: User) => void;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<User['role']>('user');
  const [status, setStatus] = useState<User['status']>('resetpassword');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setName(''); setEmail(''); setPassword(''); setRole('user'); setStatus('resetpassword'); setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required');
      return;
    }
    if (status !== 'resetpassword' && !password.trim()) {
      setError('Password is required for active/inactive users');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await UserService.createUser({ name, email, password: password || undefined, role, status });
      onCreated(res.data);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Create User"
      maxWidth="sm"
      onSubmit={handleSave}
      isLoading={saving}
      submitButtonText="Create"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <BaseTextField label="Name" value={name} onChange={(e) => setName((e.target as HTMLInputElement).value)} fullWidth size="small" />
        <BaseTextField label="Email" value={email} onChange={(e) => setEmail((e.target as HTMLInputElement).value)} fullWidth size="small" type="email" />
        <BaseSelect
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as User['status'])}
          options={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
            { label: 'Reset Password (auto-generate password)', value: 'resetpassword' },
          ]}
        />
        {status !== 'resetpassword' && (
          <BaseTextField label="Password" value={password} onChange={(e) => setPassword((e.target as HTMLInputElement).value)} fullWidth size="small" type="password" />
        )}
        <BaseSelect
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as User['role'])}
          options={[
            { label: 'User', value: 'user' },
            { label: 'Manager', value: 'manager' },
            { label: 'Admin', value: 'admin' },
          ]}
        />
        {status === 'resetpassword' && (
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: '#E65100', backgroundColor: '#FFF3E0', px: 1.5, py: 1, borderRadius: '6px' }}>
            A temporary password will be auto-generated. Copy it from the user table after creation.
          </Typography>
        )}
        {error && (
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: '#D32F2F' }}>{error}</Typography>
        )}
      </Box>
    </BaseModal>
  );
};

// --- Delete Confirm Modal ----------------------------------------------------
interface DeleteModalProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ open, loading, onClose, onConfirm }) => (
  <BaseModal
    open={open}
    onClose={onClose}
    title="Delete User"
    maxWidth="xs"
    onSubmit={onConfirm}
    isLoading={loading}
    submitButtonText="Delete"
    cancelButtonText="Cancel"
  >
    <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: TEXT_SECONDARY }}>
      Are you sure you want to delete this user? This action cannot be undone.
    </Typography>
  </BaseModal>
);

// --- Main Page ---------------------------------------------------------------
const breadcrumbs: BreadcrumbItem[] = [
  { label: 'Dashboard', path: '/admin' },
  { label: 'Users' },
];

export const UsersPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);

  // Edit modal
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Delete modal
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    // Check if current user is admin
    const checkAdmin = async () => {
      try {
        const profile = await AuthService.getProfile();
        if (profile.data) {
          setIsAdmin(profile.data.role === 'admin');
        }
      } catch (err) {
        console.error('Failed to get profile:', err);
      }
    };
    
    checkAdmin();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await UserService.getAllUsers(page + 1, rowsPerPage);
      setUsers(res.data.docs);
      setTotalUsers(res.data.totalDocs);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpen = (user: User) => {
    if (!isAdmin) {
      setError('Only admins can edit users');
      return;
    }
    setEditUser(user);
    setEditOpen(true);
  };

  const handleEditSaved = (updated: User) => {
    setUsers((prev) => prev.map((u) => (u._id === updated._id ? updated : u)));
  };

  const handleDeleteOpen = (user: User) => {
    if (!isAdmin) {
      setError('Only admins can delete users');
      return;
    }
    setDeleteId(user._id!);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await UserService.deleteUser(deleteId);
      setUsers((prev) => prev.filter((u) => u._id !== deleteId));
      setDeleteId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUserCreated = (user: User) => {
    setUsers((prev) => [user, ...prev]);
    setTotalUsers((prev) => prev + 1);
  };

  const handleCopyPassword = (password: string, userId: string) => {
    navigator.clipboard.writeText(password);
    setCopySuccess(userId);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  return (
    <Box>
      <PageHeader
        title="User Management"
        subtitle="Manage system users and assign roles"
        breadcrumbs={breadcrumbs}
      />

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleUserCreated}
      />

      <EditUserModal
        user={editUser}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={handleEditSaved}
        isAdmin={isAdmin}
      />

      <DeleteModal
        open={!!deleteId}
        loading={deleteLoading}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontFamily: FONT }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box>
          
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: TEXT_TERTIARY, mt: 0.25 }}>
            {totalUsers} user{totalUsers !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
            <BaseButton
              onClick={() => setCreateOpen(true)}
              startIcon={<PersonAddIcon />}
              sx={{ height: 36 }}
            >
              Create User
            </BaseButton>
          )}
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchUsers}
              disabled={loading}
              size="small"
              sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px', color: TEXT_SECONDARY, '&:hover': { backgroundColor: SURFACE } }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      {loading && users.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={32} sx={{ color: ACCENT }} />
        </Box>
      ) : users.length === 0 ? (
        <Box sx={{ border: `2px dashed ${BORDER}`, borderRadius: '12px', py: 8, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.9375rem', color: TEXT_TERTIARY }}>No users found</Typography>
        </Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: '10px', border: `1px solid ${BORDER}`, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Name</TableCell>
                  <TableCell sx={headerCellSx}>Email</TableCell>
                  <TableCell sx={headerCellSx}>Role</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Last Login</TableCell>
                  <TableCell sx={headerCellSx}>Created</TableCell>
                  <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user._id}
                    sx={{ '&:hover': { backgroundColor: SURFACE }, '&:last-child td': { borderBottom: 'none' } }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{user.name}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: TEXT_SECONDARY }}>{user.email}</TableCell>
                    <TableCell sx={bodyCellSx}><RoleBadge role={user.role} /></TableCell>
                    <TableCell sx={bodyCellSx}><StatusBadge status={user.status} /></TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: TEXT_TERTIARY, fontSize: '0.8125rem' }}>
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString() + ' ' + new Date(user.lastLogin).toLocaleTimeString()
                        : '�'}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: TEXT_TERTIARY, fontSize: '0.8125rem' }}>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '�'}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      {isAdmin && user.tempPassword && user.status === 'resetpassword' && (
                        <Tooltip title={copySuccess === user._id ? 'Copied!' : 'Copy password'}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyPassword(user.tempPassword!, user._id!)}
                            sx={{ color: ACCENT, '&:hover': { backgroundColor: ACCENT_LIGHT }, borderRadius: '6px' }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={isAdmin ? 'Edit user' : 'Only admins can edit'}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditOpen(user)}
                          disabled={!isAdmin}
                          sx={{ color: isAdmin ? ACCENT : TEXT_TERTIARY, '&:hover': { backgroundColor: isAdmin ? ACCENT_LIGHT : 'transparent' }, borderRadius: '6px', opacity: isAdmin ? 1 : 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isAdmin ? 'Delete user' : 'Only admins can delete'}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOpen(user)}
                          disabled={!isAdmin}
                          sx={{ color: isAdmin ? '#D32F2F' : TEXT_TERTIARY, '&:hover': { backgroundColor: isAdmin ? 'rgba(211,47,47,0.08)' : 'transparent' }, borderRadius: '6px', ml: 0.5, opacity: isAdmin ? 1 : 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              borderTop: `1px solid ${BORDER}`,
              fontFamily: FONT,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                fontFamily: FONT,
                fontSize: '0.8125rem',
                color: TEXT_SECONDARY,
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default UsersPage;

